import * as THREE from 'three';

export type SegmentState = 'empty' | 'building' | 'complete';

export interface DysonMaterialConfig {
  baseColor: THREE.Color;
  emissiveColor: THREE.Color;
  metalness: number;
  roughness: number;
  opacity: {
    empty: number;
    building: number;
    complete: number;
  };
  emissive: {
    empty: number;
    building: number;
    complete: number;
  };
  animation: {
    constructionSpeed: number;
    pulseFrequency: number;
    glowIntensity: number;
  };
}

export interface SegmentMaterialState {
  state: SegmentState;
  progress: number; // 0-1 for building segments
  poolColor?: THREE.Color; // Mining pool color
  lastUpdate: number;
}

export class DysonMaterial {
  private config: DysonMaterialConfig;
  private materials: Map<SegmentState, THREE.ShaderMaterial> = new Map();
  private time: number = 0;
  
  constructor(config?: Partial<DysonMaterialConfig>) {
    this.config = {
      baseColor: new THREE.Color(0x4a90e2), // Metallic blue
      emissiveColor: new THREE.Color(0x00aaff), // Electric blue
      metalness: 0.9,
      roughness: 0.1,
      opacity: {
        empty: 0.0,
        building: 0.4,
        complete: 0.9,
      },
      emissive: {
        empty: 0.0,
        building: 0.6,
        complete: 0.2,
      },
      animation: {
        constructionSpeed: 2.0,
        pulseFrequency: 1.5,
        glowIntensity: 1.0,
      },
      ...config,
    };
    
    this.createMaterials();
  }
  
  private createMaterials(): void {
    // Empty segment material (invisible/transparent)
    this.materials.set('empty', this.createSegmentMaterial('empty'));
    
    // Building segment material (animated, glowing)
    this.materials.set('building', this.createSegmentMaterial('building'));
    
    // Complete segment material (solid, metallic)
    this.materials.set('complete', this.createSegmentMaterial('complete'));
  }
  
  private createSegmentMaterial(state: SegmentState): THREE.ShaderMaterial {
    const uniforms = {
      time: { value: 0.0 },
      baseColor: { value: this.config.baseColor.clone() },
      emissiveColor: { value: this.config.emissiveColor.clone() },
      metalness: { value: this.config.metalness },
      roughness: { value: this.config.roughness },
      opacity: { value: this.config.opacity[state] },
      emissiveIntensity: { value: this.config.emissive[state] },
      constructionProgress: { value: 0.0 },
      poolColor: { value: new THREE.Color(0xffffff) },
      pulseFrequency: { value: this.config.animation.pulseFrequency },
      glowIntensity: { value: this.config.animation.glowIntensity },
    };
    
    return new THREE.ShaderMaterial({
      uniforms,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader(state),
      transparent: true,
      side: THREE.DoubleSide,
      blending: state === 'building' ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
  }
  
  private getVertexShader(): string {
    return `
      varying vec3 vPosition;
      varying vec3 vNormal;
      varying vec2 vUv;
      varying vec3 vWorldPosition;
      
      void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vPosition = position;
        
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
  }
  
  private getFragmentShader(state: SegmentState): string {
    const baseShader = `
      uniform float time;
      uniform vec3 baseColor;
      uniform vec3 emissiveColor;
      uniform vec3 poolColor;
      uniform float metalness;
      uniform float roughness;
      uniform float opacity;
      uniform float emissiveIntensity;
      uniform float constructionProgress;
      uniform float pulseFrequency;
      uniform float glowIntensity;
      
      varying vec3 vPosition;
      varying vec3 vNormal;
      varying vec2 vUv;
      varying vec3 vWorldPosition;
      
      // Simple noise function
      float noise(vec3 p) {
        return fract(sin(dot(p, vec3(12.9898, 78.233, 45.164))) * 43758.5453);
      }
      
      void main() {
        vec3 color = baseColor;
        vec3 emissive = emissiveColor;
        float alpha = opacity;
    `;
    
    switch (state) {
      case 'empty':
        return baseShader + `
          // Empty segments are completely transparent
          alpha = 0.0;
          
          gl_FragColor = vec4(color, alpha);
        }`;
      
      case 'building':
        return baseShader + `
          // Building segments have animated construction effects
          
          // Construction wave effect
          float wave = sin(vUv.y * 10.0 + time * 2.0) * 0.5 + 0.5;
          float constructionMask = step(vUv.y, constructionProgress + wave * 0.1);
          
          // Pulse effect
          float pulse = sin(time * pulseFrequency * 6.28318) * 0.5 + 0.5;
          
          // Mix pool color with base color
          color = mix(baseColor, poolColor, 0.3);
          
          // Add construction effects
          emissive = mix(emissiveColor, poolColor, 0.5);
          emissive *= emissiveIntensity * (1.0 + pulse * 0.5);
          
          // Alpha based on construction progress and pulse
          alpha = opacity * constructionMask * (0.7 + pulse * 0.3);
          
          // Add sparks/particles effect
          float sparkle = noise(vWorldPosition * 50.0 + time * 5.0);
          if (sparkle > 0.95 && constructionMask > 0.5) {
            emissive += vec3(1.0) * 2.0;
          }
          
          color += emissive;
          
          gl_FragColor = vec4(color, alpha);
        }`;
      
      case 'complete':
        return baseShader + `
          // Complete segments have subtle metallic appearance
          
          // Subtle energy flow
          float flow = sin(vUv.x * 8.0 + time * 0.5) * 0.1 + 0.9;
          
          // Metallic reflection simulation
          vec3 viewDir = normalize(cameraPosition - vWorldPosition);
          float fresnel = pow(1.0 - max(0.0, dot(viewDir, vNormal)), 2.0);
          
          // Apply metallic properties
          color = mix(color, vec3(1.0), metalness * fresnel * 0.3);
          
          // Subtle emissive glow
          emissive *= emissiveIntensity * flow;
          color += emissive;
          
          // Full opacity for complete segments
          alpha = opacity;
          
          gl_FragColor = vec4(color, alpha);
        }`;
      
      default:
        return baseShader + `
          gl_FragColor = vec4(color, alpha);
        }`;
    }
  }
  
  public getMaterial(state: SegmentState): THREE.ShaderMaterial {
    const material = this.materials.get(state);
    if (!material) {
      throw new Error(`Material for state '${state}' not found`);
    }
    return material;
  }
  
  public updateSegmentMaterial(
    material: THREE.ShaderMaterial,
    segmentState: SegmentMaterialState
  ): void {
    const { state, progress, poolColor } = segmentState;
    
    // Update construction progress
    material.uniforms['constructionProgress'].value = progress;
    
    // Update pool color if provided
    if (poolColor) {
      material.uniforms['poolColor'].value.copy(poolColor);
    }
    
    // Update state-specific properties
    material.uniforms['opacity'].value = this.config.opacity[state];
    material.uniforms['emissiveIntensity'].value = this.config.emissive[state];
    
    // Update blending mode
    if (state === 'building') {
      material.blending = THREE.AdditiveBlending;
    } else {
      material.blending = THREE.NormalBlending;
    }
    
    material.transparent = state !== 'complete' || this.config.opacity[state] < 1.0;
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms['time'].value = this.time;
    }
  }
  
  public setPoolColor(color: THREE.Color): void {
    // Update all materials with new pool color
    for (const material of this.materials.values()) {
      material.uniforms['poolColor'].value.copy(color);
    }
  }
  
  public setConstructionSpeed(speed: number): void {
    this.config.animation.constructionSpeed = speed;
    
    // Update pulse frequency based on construction speed
    const pulseFreq = this.config.animation.pulseFrequency * speed;
    for (const material of this.materials.values()) {
      material.uniforms['pulseFrequency'].value = pulseFreq;
    }
  }
  
  public setGlowIntensity(intensity: number): void {
    this.config.animation.glowIntensity = intensity;
    
    for (const material of this.materials.values()) {
      material.uniforms['glowIntensity'].value = intensity;
    }
  }
  
  public createInstancedMaterial(state: SegmentState, instanceCount: number): THREE.ShaderMaterial {
    const baseMaterial = this.getMaterial(state);
    
    // Clone the material for instanced rendering
    const instancedMaterial = baseMaterial.clone();
    
    // Add instance-specific uniforms
    const instanceColors = new Float32Array(instanceCount * 3);
    const instanceProgress = new Float32Array(instanceCount);
    const instanceStates = new Float32Array(instanceCount);
    
    // Initialize with default values
    for (let i = 0; i < instanceCount; i++) {
      const i3 = i * 3;
      instanceColors[i3] = 1.0;     // R
      instanceColors[i3 + 1] = 1.0; // G
      instanceColors[i3 + 2] = 1.0; // B
      instanceProgress[i] = 0.0;
      instanceStates[i] = 0.0; // 0=empty, 1=building, 2=complete
    }
    
    instancedMaterial.uniforms['instanceColors'] = { value: instanceColors };
    instancedMaterial.uniforms['instanceProgress'] = { value: instanceProgress };
    instancedMaterial.uniforms['instanceStates'] = { value: instanceStates };
    
    return instancedMaterial;
  }
  
  public updateInstancedMaterial(
    material: THREE.ShaderMaterial,
    instanceIndex: number,
    segmentState: SegmentMaterialState
  ): void {
    const { state, progress, poolColor } = segmentState;
    
    if (material.uniforms['instanceProgress']) {
      material.uniforms['instanceProgress'].value[instanceIndex] = progress;
    }
    
    if (material.uniforms['instanceStates']) {
      let stateValue = 0.0;
      switch (state) {
        case 'empty': stateValue = 0.0; break;
        case 'building': stateValue = 1.0; break;
        case 'complete': stateValue = 2.0; break;
      }
      material.uniforms['instanceStates'].value[instanceIndex] = stateValue;
    }
    
    if (material.uniforms['instanceColors'] && poolColor) {
      const i3 = instanceIndex * 3;
      material.uniforms['instanceColors'].value[i3] = poolColor.r;
      material.uniforms['instanceColors'].value[i3 + 1] = poolColor.g;
      material.uniforms['instanceColors'].value[i3 + 2] = poolColor.b;
    }
  }
  
  public dispose(): void {
    for (const material of this.materials.values()) {
      material.dispose();
    }
    this.materials.clear();
  }
}

// Factory function for common material configurations
export const createDysonMaterial = {
  standard: () => new DysonMaterial(),
  
  highContrast: () => new DysonMaterial({
    emissive: {
      empty: 0.0,
      building: 1.0,
      complete: 0.3,
    },
    animation: {
      constructionSpeed: 3.0,
      pulseFrequency: 2.0,
      glowIntensity: 1.5,
    },
  }),
  
  subtle: () => new DysonMaterial({
    opacity: {
      empty: 0.0,
      building: 0.2,
      complete: 0.7,
    },
    emissive: {
      empty: 0.0,
      building: 0.3,
      complete: 0.1,
    },
    animation: {
      constructionSpeed: 1.0,
      pulseFrequency: 0.8,
      glowIntensity: 0.5,
    },
  }),
};